import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { demands, type Pending, type PendingArea } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime, formatRelative } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { grupoDaArea } from './acompanhamento';

const areas: { value: PendingArea; label: string }[] = [
  { value: 'credito', label: 'Credito' },
  { value: 'subscricao', label: 'Subscricao' },
  { value: 'juridico', label: 'Juridico' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'documentos', label: 'Documentos' },
  { value: 'operacional', label: 'Operacional' },
  // Origens do Follow up do OnPoint que nao sao etapa (P-COT-3).
  { value: 'cadastro', label: 'Cadastro' },
  { value: 'pep', label: 'PEP' },
  { value: 'resseguro', label: 'Resseguro' },
];

/**
 * Pendencias da operacao.
 *
 * A pendencia nasce aberta e so sai por resolucao explicita, com autor e data.
 * Uma pendencia que sumisse ao avancar a etapa deixaria de responder por que a
 * operacao ficou parada.
 */
export function PendingsPanel({
  operationId,
  origem,
}: {
  operationId: string;
  /** Filtro de origem do Follow up (P-COT-3): so as pendencias desse grupo. */
  origem?: string | undefined;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [area, setArea] = useState<PendingArea>('documentos');
  const [descricao, setDescricao] = useState('');
  // A resolucao tem texto proprio (defeito 11 de "Operacao e esteira" do 47, anexo
  // C): antes ela ia sempre vazia, e a pendencia resolvida nao dizia como.
  const [resolvendo, setResolvendo] = useState<Pending | null>(null);
  const [resolucao, setResolucao] = useState('');

  const podeGerenciar = can('demandas.demanda.update');

  const consulta = useQuery({
    queryKey: ['pendings', operationId],
    queryFn: ({ signal }) => demands.pendings(operationId, signal),
    enabled: operationId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['pendings', operationId] });
    void queryClient.invalidateQueries({ queryKey: ['demands'] });
    void queryClient.invalidateQueries({ queryKey: ['operations', operationId] });
  }

  const abrir = useMutation({
    mutationFn: () => demands.openPending(operationId, area, descricao),
    onSuccess: () => {
      toast.success('Pendencia registrada.');
      setDescricao('');
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const resolver = useMutation({
    mutationFn: (pending: Pending) =>
      demands.resolvePending(operationId, pending.id, resolucao),
    onSuccess: () => {
      toast.success('Pendencia resolvida.');
      setResolvendo(null);
      setResolucao('');
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function registrar(event: FormEvent) {
    event.preventDefault();
    abrir.mutate();
  }

  const itens = (consulta.data ?? []).filter(
    (pending) => !origem || grupoDaArea(pending.area) === origem,
  );
  const abertas = itens.filter((pending) => pending.status === 'aberta');

  return (
    <Card>
      <CardHeader
        title="Pendencias"
        description="Exigencias registradas pelas areas."
        action={
          abertas.length > 0 ? <StatusBadge kind="pendencia" value="aberta" /> : null
        }
      />

      {consulta.isError ? (
        <CardBody>
          <Alert tone="danger" title="Nao foi possivel carregar as pendencias">
            {consulta.error.message}
          </Alert>
        </CardBody>
      ) : null}

      {consulta.isPending ? (
        <CardBody>
          <SkeletonText lines={3} />
        </CardBody>
      ) : itens.length === 0 ? (
        <EmptyState
          icon="check"
          title="Nenhuma pendencia registrada"
          description="Quando uma area precisar de algo para seguir, registre aqui."
        />
      ) : (
        <ul className="divide-y divide-line">
          {itens.map((pending) => (
            <li key={pending.id} className="flex items-start gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2">
                  <StatusBadge kind="area" value={pending.area} />
                  <StatusBadge kind="pendencia" value={pending.status} />
                </p>
                <p className="mt-1 text-sm text-ink-800">{pending.description}</p>
                <p className="mt-0.5 text-2xs text-ink-500">
                  {pending.opened_label ? `${pending.opened_label} - ` : ''}
                  <time
                    dateTime={pending.created_at}
                    title={formatDateTime(pending.created_at)}
                  >
                    {formatRelative(pending.created_at)}
                  </time>
                  {pending.resolved_label
                    ? ` - resolvida por ${pending.resolved_label}`
                    : ''}
                  {pending.resolved_at
                    ? ` em ${formatDateTime(pending.resolved_at)}`
                    : ''}
                </p>
                {pending.resolution ? (
                  <p className="mt-1 text-xs text-ink-600">
                    Resolucao: {pending.resolution}
                  </p>
                ) : null}
              </div>

              {podeGerenciar && pending.status === 'aberta' ? (
                <AcaoComIcone
                  icone="check"
                  tom="success"
                  rotulo="Resolver"
                  disabled={resolver.isPending}
                  onClick={() => {
                    setResolucao('');
                    setResolvendo(pending);
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {podeGerenciar ? (
        <CardBody className="border-t border-line">
          <form onSubmit={registrar} className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
              <Select
                label="Area"
                options={areas}
                value={area}
                onChange={(event) => setArea(event.target.value as PendingArea)}
              />
              <Field
                label="O que falta"
                required
                value={descricao}
                placeholder="Descreva a exigencia"
                onChange={(event) => setDescricao(event.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                loading={abrir.isPending}
                disabled={descricao.trim() === ''}
              >
                Registrar pendencia
              </Button>
            </div>
          </form>
        </CardBody>
      ) : null}

      <Modal
        open={resolvendo !== null}
        onClose={() => setResolvendo(null)}
        title="Resolver pendencia"
        description={resolvendo?.description}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setResolvendo(null)}>
              Cancelar
            </Button>
            <Button
              loading={resolver.isPending}
              onClick={() => {
                if (resolvendo) resolver.mutate(resolvendo);
              }}
            >
              Resolver
            </Button>
          </>
        }
      >
        <Textarea
          label="Resolucao"
          value={resolucao}
          hint="Como a pendencia foi atendida. Fica na pendencia e na linha do tempo."
          onChange={(event) => setResolucao(event.target.value)}
        />
      </Modal>
    </Card>
  );
}

/**
 * Conversa da operacao.
 *
 * Separada da linha do tempo de proposito: o historico registra o que aconteceu
 * com a garantia e e consultado como prova. Misturar conversa de trabalho o
 * tornaria ilegivel justamente quando mais importa.
 */
export function CommentsPanel({
  operationId,
  originPipeId,
  title = 'Conversa',
  description = 'Mensagens de trabalho da operacao. Nao entram na linha do tempo.',
  origem,
}: {
  operationId: string;
  /** Filtro de origem do Follow up (P-COT-3): so as mensagens dessa origem. */
  origem?: string | undefined;
  /**
   * Esteira de onde a mensagem sai (Follow up da gaveta do cartao, P-EST-1). Com
   * ela, a mensagem grava a origem e pode ter titulo.
   */
  originPipeId?: string | undefined;
  title?: string;
  description?: string;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState('');
  const [titulo, setTitulo] = useState('');

  const podeComentar = can('operacoes.comentario.create');

  const consulta = useQuery({
    queryKey: ['comments', operationId],
    queryFn: ({ signal }) => demands.comments(operationId, signal),
    enabled: operationId !== '',
  });

  const enviar = useMutation({
    mutationFn: () =>
      demands.comment(
        operationId,
        originPipeId
          ? { body: texto, origin_pipe_id: originPipeId, title: titulo.trim() }
          : texto,
      ),
    onSuccess: () => {
      setTexto('');
      setTitulo('');
      void queryClient.invalidateQueries({ queryKey: ['comments', operationId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submeter(event: FormEvent) {
    event.preventDefault();
    enviar.mutate();
  }

  const mensagens = (consulta.data ?? []).filter(
    (mensagem) => !origem || (mensagem.source ?? 'cotacao') === origem,
  );

  return (
    <Card>
      <CardHeader title={title} description={description} />

      {consulta.isPending ? (
        <CardBody>
          <SkeletonText lines={3} />
        </CardBody>
      ) : mensagens.length === 0 ? (
        <EmptyState
          icon="users"
          title="Nenhuma mensagem"
          description="Use a conversa para alinhar o andamento sem poluir o historico."
        />
      ) : (
        <ul className="flex max-h-80 flex-col gap-3 overflow-y-auto px-5 py-4">
          {mensagens.map((mensagem) => (
            <li key={mensagem.id} className="rounded-md bg-surface-sunken px-3 py-2">
              {/* A origem diz de qual esteira saiu o registro (Origem do Follow up
                  do OnPoint, P-EST-1). */}
              <p className="mb-1 flex flex-wrap items-center gap-1.5 text-2xs text-ink-500">
                <span>Origem</span>
                <StatusBadge
                  kind="origemFollowUp"
                  value={mensagem.source ?? 'cotacao'}
                />
                {mensagem.title ? (
                  <span className="font-medium text-ink-700">{mensagem.title}</span>
                ) : null}
              </p>
              <p className="text-sm text-ink-800">{mensagem.body}</p>
              <p className="mt-1 text-2xs text-ink-500">
                {mensagem.author_label || 'Autor removido'} -{' '}
                <time
                  dateTime={mensagem.created_at}
                  title={formatDateTime(mensagem.created_at)}
                >
                  {formatRelative(mensagem.created_at)}
                </time>
              </p>
            </li>
          ))}
        </ul>
      )}

      {podeComentar ? (
        <CardBody className="border-t border-line">
          <form onSubmit={submeter} className="flex flex-wrap items-end gap-2">
            {originPipeId ? (
              <div className="w-full">
                <Field
                  label="Titulo do registro"
                  value={titulo}
                  maxLength={160}
                  placeholder="Ex.: Analise de mesa de cadastro"
                  onChange={(event) => setTitulo(event.target.value)}
                />
              </div>
            ) : null}
            <div className="flex-1">
              <Field
                label="Nova mensagem"
                value={texto}
                placeholder={originPipeId ? 'Digite aqui' : 'Escreva uma mensagem'}
                onChange={(event) => setTexto(event.target.value)}
              />
            </div>
            <Button
              type="submit"
              loading={enviar.isPending}
              disabled={texto.trim() === ''}
            >
              Enviar
            </Button>
          </form>
        </CardBody>
      ) : null}
    </Card>
  );
}
